import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buttonReset, color, font, radius, shadow, space, weight } from '../theme';
import Icon from './Icon';

interface ChipProps {
  children: React.ReactNode;
  selected?: boolean;
  onClick: () => void;
  count?: number;
  // 'raised' sits on the page background; 'flat' sits inside a card
  variant?: 'raised' | 'flat';
  // Fill the container's width (chips in a grid of equal cells)
  block?: boolean;
  // 'sm': a 32px pill for compact groups that wrap (still a 44px tap target)
  size?: 'md' | 'sm';
}

// The selected chip: light blue with a check mark. Solid blue is kept for the one primary
// control on a screen, so a row of filters never stacks up several solid-blue chips.
const SELECTED_BG = color.primarySoft;
const SELECTED_RING = 'inset 0 0 0 1.5px rgba(24,95,165,0.45)';

/* A filter toggle. The visible pill is 36px tall; the button's vertical padding makes
   the tap target 44px. Selected is blue (never orange). */
const Chip = ({ children, selected = false, onClick, count, variant = 'raised', block = false, size = 'md' }: ChipProps) => {
  const sm = size === 'sm';
  return (
  <button type="button" onClick={onClick} aria-pressed={selected} style={{ ...buttonReset, flex: '0 0 auto', padding: sm ? '6px 0' : '4px 0', width: block ? '100%' : undefined }}>
    <span style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: block ? 'center' : undefined,
      gap: sm ? space.xs + 1 : space.xs + 2,
      height: sm ? 32 : 36,
      padding: sm ? `0 ${space.sm + 3}px` : `0 ${space.md + 2}px`,
      borderRadius: radius.pill,
      fontSize: sm ? font.small : font.label,
      fontWeight: selected ? weight.bold : weight.medium,
      whiteSpace: 'nowrap',
      color: selected ? color.primary : color.text,
      background: selected ? SELECTED_BG : variant === 'flat' ? color.surfaceMuted : color.surface,
      boxShadow: selected ? SELECTED_RING : variant === 'raised' ? shadow.raised : 'none',
      transition: 'background 0.15s, color 0.15s',
    }}>
      {selected && <Icon name="check" size={sm ? 13 : 15} strokeWidth={2.4} />}
      {children}
      {count !== undefined && (
        <span style={{ fontSize: font.caption, fontWeight: weight.medium, color: selected ? color.primary : color.textSecondary }}>{count.toLocaleString('he-IL')}</span>
      )}
    </span>
  </button>
  );
};

// Fade an edge of a scrolling row when more chips are hidden past it (RTL: the row starts on
// the right, so the hidden chips are usually on the left)
const fadeMask = (fadeRight: boolean, fadeLeft: boolean): string | undefined => {
  if (!fadeRight && !fadeLeft) return undefined;
  const right = fadeRight ? 'transparent 0, #000 28px' : '#000 0';
  const left = fadeLeft ? '#000 calc(100% - 28px), transparent 100%' : '#000 100%';
  return `linear-gradient(to left, ${right}, ${left})`;
};

// A horizontally scrolling row of chips. When chips are hidden off an edge, that edge fades.
export const ChipRow = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [fade, setFade] = useState({ right: false, left: false });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const hidden = el.scrollWidth - el.clientWidth;
    if (hidden <= 1) { setFade(f => (f.right || f.left ? { right: false, left: false } : f)); return; }
    // In RTL, scrollLeft is 0 at the start and goes negative towards the end
    const scrolled = Math.abs(el.scrollLeft);
    const next = { right: scrolled > 1, left: scrolled < hidden - 1 };
    setFade(f => (f.right === next.right && f.left === next.left ? f : next));
  }, []);
  useEffect(() => {
    update();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update]);
  useEffect(update);
  const mask = fadeMask(fade.right, fade.left);
  return (
    <div
      ref={ref}
      onScroll={update}
      style={{ display: 'flex', gap: space.sm, overflowX: 'auto', scrollbarWidth: 'none', WebkitMaskImage: mask, maskImage: mask, ...style }}
    >
      {children}
    </div>
  );
};

export default Chip;
